import { useCallback, useEffect, useState } from 'react'
import { useSnapshot } from 'valtio'
import { hideCurrentModal, miscUiState } from '../globalState'
import { downloadAndOpenFlashbackGame } from '../downloadAndOpenFile'
import { useIsModalActive } from './utilsApp'
import Button from './Button'
import styles from './replayArchive.module.css'

type ReplayGame = {
  id: string
  isBeta: boolean
  serverId: string
  winner: string
  winnerKills: number
  kit: string
  players: number
  startedAt: string
  duration: string
  replay: { sizeBytes: number, recordingMode: string }
}

type ReplayArchive = {
  games: ReplayGame[]
  selectedDate: string
  latestDate: string
  previousDate: string | null
  nextDate: string | null
  includeBeta: boolean
}

const formatDate = (date: string) => new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
}).format(new Date(`${date}T00:00:00Z`))

export default function ReplayArchiveProvider () {
  const active = useIsModalActive('replay-archive')
  const { appConfig } = useSnapshot(miscUiState)
  const [archive, setArchive] = useState<ReplayArchive>()
  const [includeBeta, setIncludeBeta] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (date?: string, beta = includeBeta) => {
    setLoading(true)
    setError('')
    try {
      const base = (appConfig?.replayApiBaseUrl ?? 'https://www.hg-pvp.net').replace(/\/$/, '')
      const params = new URLSearchParams()
      if (date) params.set('date', date)
      if (beta) params.set('beta', '1')
      const response = await fetch(`${base}/api/replay-player/games?${params.toString()}`)
      if (!response.ok) throw new Error(`API indisponible (${response.status})`)
      setArchive(await response.json())
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Impossible de charger les parties')
    } finally {
      setLoading(false)
    }
  }, [appConfig?.replayApiBaseUrl, includeBeta])

  useEffect(() => {
    if (active) load(undefined, includeBeta).catch(() => {})
  }, [active])

  if (!active) return null

  const changeBeta = (enabled: boolean) => {
    setIncludeBeta(enabled)
    void load(archive?.selectedDate, enabled)
  }

  return <div className={styles.screen}>
    <section className={styles.panel}>
      <header>
        <div><small>ARCHIVES DE L&apos;ARÈNE</small><h1>Liste des parties</h1><p>Sélectionne un replay FlashBack HG-PvP.</p></div>
        <Button className={styles.close} onClick={() => hideCurrentModal()}>Retour</Button>
      </header>
      <nav className={styles.navigation}>
        <Button disabled={!archive?.previousDate || loading} onClick={async () => { if (archive?.previousDate) await load(archive.previousDate) }}>‹</Button>
        <label>
          <span>{archive ? formatDate(archive.selectedDate) : 'Chargement…'}</span>
          <input type='date' value={archive?.selectedDate ?? ''} max={archive?.latestDate} onChange={async event => { await load(event.target.value) }} />
        </label>
        <Button disabled={!archive?.nextDate || loading} onClick={async () => { if (archive?.nextDate) await load(archive.nextDate) }}>›</Button>
        <label className={styles.beta}><input type='checkbox' checked={includeBeta} onChange={event => changeBeta(event.target.checked)} /> Inclure la bêta</label>
      </nav>
      <div className={styles.list}>
        {loading && <div className={styles.message}>Chargement des replays…</div>}
        {error && <div className={`${styles.message} ${styles.error}`}>{error}</div>}
        {!loading && !error && archive?.games.map(game => <button className={styles.game} key={game.id} onClick={async () => { await downloadAndOpenFlashbackGame(game.id) }}>
          <b>#{game.id}</b>
          <span><strong>{game.winner || 'Partie terminée'}</strong><small>{game.serverId}{game.isBeta ? ' · BÊTA' : ''}</small></span>
          <span><strong>{game.kit}</strong><small>{game.winnerKills} kill{game.winnerKills > 1 ? 's' : ''}</small></span>
          <span><strong>{game.players} joueurs</strong><small>{game.duration} · {game.startedAt}</small></span>
          <em>{(game.replay.sizeBytes / 1024 / 1024).toFixed(1)} Mo</em>
        </button>)}
        {!loading && !error && archive && !archive.games.length && <div className={styles.message}>Aucun replay disponible pour cette journée.</div>}
      </div>
    </section>
  </div>
}
